import type { ApolloError } from '@apollo/client'
import { useMutation } from '@apollo/client'
import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Separator
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import type { ControllerRenderProps } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { DISABLE_APP, ENABLE_THREE_PL_SYSTEMS_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

const serviceFormSchema = z.object({
  baseURL: z.string().min(1, 'Base URL is required'),
  clientID: z.string().min(1, 'Client ID is required'),
  clientSecret: z.string().min(1, 'Client Secret is required')
})

const portalFormSchema = z.object({
  baseURL: z.string().min(1, 'Base URL is required'),
  password: z.string().min(1, 'Password is required'),
  username: z.string().min(1, 'Username is required')
})

type EnableThreePLSystemsServiceInput = {
  appType: AppTypeEnum
  baseURL: string
  clientID: string
  clientSecret: string
}

type EnableThreePLSystemsPortalInput = {
  appType: AppTypeEnum
  baseURL: string
  password: string
  username: string
}

/**
 * Extracts the error message from ApolloError, checking graphQLErrors first, then falling back to the main message
 */
const getErrorMessage = (err: ApolloError): string => {
  if (err.graphQLErrors && err.graphQLErrors.length > 0) {
    const graphQLError = err.graphQLErrors[0]
    if (graphQLError.message) {
      return graphQLError.message
    }
    const extensions = graphQLError.extensions as { response?: { body?: { message?: string } } } | undefined
    if (extensions?.response?.body?.message) {
      return extensions.response.body.message
    }
  }
  return err.message || 'Unknown error occurred'
}

/**
 * This is the modal for the 3PL Systems TMS app.
 * It displays two side-by-side sections for Service and Portal, each with their own Enable/Disable buttons.
 */
export function ThreePLSystemsModal({
  isOpen,
  onClose,
  serviceWorkspaceApp,
  portalWorkspaceApp,
  isModalEditable,
  setModalEditable,
  app
}: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [serviceFormError, setServiceFormError] = useState('')
  const [portalFormError, setPortalFormError] = useState('')

  const isServiceEnabled = serviceWorkspaceApp?.enabled ?? false
  const isPortalEnabled = portalWorkspaceApp?.enabled ?? false

  const serviceForm = useForm<z.infer<typeof serviceFormSchema>>({
    defaultValues: {
      baseURL: '',
      clientID: '',
      clientSecret: ''
    },
    resolver: zodResolver(serviceFormSchema)
  })

  const portalForm = useForm<z.infer<typeof portalFormSchema>>({
    defaultValues: {
      baseURL: '',
      password: '',
      username: ''
    },
    resolver: zodResolver(portalFormSchema)
  })

  useEffect(() => {
    if (!isOpen) {
      serviceForm.reset({
        baseURL: '',
        clientID: '',
        clientSecret: ''
      })
      portalForm.reset({
        baseURL: '',
        password: '',
        username: ''
      })
      return
    }

    if (isModalEditable && isOpen) {
      serviceForm.reset({
        baseURL: '',
        clientID: '',
        clientSecret: ''
      })
      portalForm.reset({
        baseURL: '',
        password: '',
        username: ''
      })
    } else if (!isModalEditable && isOpen) {
      serviceForm.reset({
        baseURL: '',
        clientID: '',
        clientSecret: ''
      })
      portalForm.reset({
        baseURL: '',
        password: '',
        username: ''
      })
    }
  }, [
    isOpen,
    isModalEditable,
    isServiceEnabled,
    isPortalEnabled,
    serviceWorkspaceApp,
    portalWorkspaceApp,
    serviceForm,
    portalForm
  ])

  const [enableServiceApp, { loading: enableServiceLoading }] = useMutation<
    { enableThreePLSystemsApp: { appEnabled: boolean } },
    EnableThreePLSystemsServiceInput
  >(ENABLE_THREE_PL_SYSTEMS_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success(
        isModalEditable
          ? '3PL Systems Service app updated successfully!'
          : '3PL Systems Service app enabled successfully!'
      )
      setServiceFormError('')
      if (isModalEditable && setModalEditable) {
        setModalEditable(false)
      }
      if (!isModalEditable) {
        serviceForm.reset()
      }
    },
    onError: (err: ApolloError) => {
      const backendErrorMessage = getErrorMessage(err)
      const errorMessage =
        backendErrorMessage && backendErrorMessage !== 'Unknown error occurred'
          ? backendErrorMessage
          : `Something went wrong. Please contact support@chaineapp.com. Error: ${backendErrorMessage}`
      setServiceFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const [enablePortalApp, { loading: enablePortalLoading }] = useMutation<
    { enableThreePLSystemsApp: { appEnabled: boolean } },
    EnableThreePLSystemsPortalInput
  >(ENABLE_THREE_PL_SYSTEMS_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success(
        isModalEditable
          ? '3PL Systems Portal app updated successfully!'
          : '3PL Systems Portal app enabled successfully!'
      )
      setPortalFormError('')
      if (isModalEditable && setModalEditable) {
        setModalEditable(false)
      }
      if (!isModalEditable) {
        portalForm.reset()
      }
    },
    onError: (err: ApolloError) => {
      const backendErrorMessage = getErrorMessage(err)
      const errorMessage =
        backendErrorMessage && backendErrorMessage !== 'Unknown error occurred'
          ? backendErrorMessage
          : `Something went wrong. Please contact support@chaineapp.com. Error: ${backendErrorMessage}`
      setPortalFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const [disableServiceApp, { loading: disableServiceLoading }] = useMutation(DISABLE_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success('3PL Systems Service app disabled successfully!')
      setServiceFormError('')
    },
    onError: (err: ApolloError) => {
      const backendErrorMessage = getErrorMessage(err)
      const errorMessage =
        backendErrorMessage && backendErrorMessage !== 'Unknown error occurred'
          ? backendErrorMessage
          : `Something went wrong. Please contact support@chaineapp.com. Error: ${backendErrorMessage}`
      setServiceFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const [disablePortalApp, { loading: disablePortalLoading }] = useMutation(DISABLE_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success('3PL Systems Portal app disabled successfully!')
      setPortalFormError('')
    },
    onError: (err: ApolloError) => {
      const backendErrorMessage = getErrorMessage(err)
      const errorMessage =
        backendErrorMessage && backendErrorMessage !== 'Unknown error occurred'
          ? backendErrorMessage
          : `Something went wrong. Please contact support@chaineapp.com. Error: ${backendErrorMessage}`
      setPortalFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const handleClose = () => {
    setServiceFormError('')
    setPortalFormError('')
    serviceForm.reset()
    portalForm.reset()
    if (isModalEditable && setModalEditable) {
      setModalEditable(false)
    }
    onClose()
  }

  const onServiceSubmit = (values: z.infer<typeof serviceFormSchema>) => {
    enableServiceApp({
      variables: {
        appType: AppTypeEnum.ThreePLSystems,
        baseURL: values.baseURL,
        clientID: values.clientID,
        clientSecret: values.clientSecret
      }
    })
  }

  const onPortalSubmit = (values: z.infer<typeof portalFormSchema>) => {
    enablePortalApp({
      variables: {
        appType: AppTypeEnum.ThreePLSystemsPortal,
        baseURL: values.baseURL,
        password: values.password,
        username: values.username
      }
    })
  }

  const handleDisableService = () => {
    disableServiceApp({
      context: {
        headers: {
          'ws-id': serviceWorkspaceApp?.workspaceID || ''
        }
      },
      variables: { appType: AppTypeEnum.ThreePLSystems }
    })
  }

  const handleDisablePortal = () => {
    disablePortalApp({
      context: {
        headers: {
          'ws-id': portalWorkspaceApp?.workspaceID || ''
        }
      },
      variables: { appType: AppTypeEnum.ThreePLSystemsPortal }
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl w-full max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isModalEditable ? 'Update' : 'Enable'} 3PL Systems for Chain</DialogTitle>
          <DialogDescription>
            {isModalEditable
              ? 'Update 3PL Systems Service and Portal integrations. Each can be updated or disabled independently.'
              : 'Enable 3PL Systems Service and Portal integrations. Each can be enabled or disabled independently.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-row gap-6 mt-4 min-w-0">
          {/* Service Section */}
          <div className="flex-1 space-y-4 min-w-0">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Service (API)</h3>
              {!!isServiceEnabled && <Badge className="bg-green-500 text-white border-green-600">Enabled</Badge>}
            </div>
            <p className="text-sm text-muted-foreground">Use Client ID and Client Secret for API access.</p>

            <Form {...serviceForm}>
              <div className="space-y-4">
                <FormField
                  control={serviceForm.control}
                  name="baseURL"
                  render={({
                    field
                  }: {
                    field: ControllerRenderProps<z.infer<typeof serviceFormSchema>, 'baseURL'>
                  }) => (
                    <FormItem>
                      <FormLabel>Base URL</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="yourcompany.3plsystems.com"
                          {...field}
                          disabled={
                            enableServiceLoading || disableServiceLoading || (isServiceEnabled && !isModalEditable)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={serviceForm.control}
                  name="clientID"
                  render={({
                    field
                  }: {
                    field: ControllerRenderProps<z.infer<typeof serviceFormSchema>, 'clientID'>
                  }) => (
                    <FormItem>
                      <FormLabel>Client ID</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter your Client ID"
                          {...field}
                          disabled={
                            enableServiceLoading || disableServiceLoading || (isServiceEnabled && !isModalEditable)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={serviceForm.control}
                  name="clientSecret"
                  render={({
                    field
                  }: {
                    field: ControllerRenderProps<z.infer<typeof serviceFormSchema>, 'clientSecret'>
                  }) => (
                    <FormItem>
                      <FormLabel>Client Secret</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Enter your Client Secret"
                          {...field}
                          disabled={
                            enableServiceLoading || disableServiceLoading || (isServiceEnabled && !isModalEditable)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!!serviceFormError && <Alert variant="error" description={serviceFormError} />}

                <div className="flex justify-end gap-2">
                  {isServiceEnabled && !isModalEditable ? (
                    <Button variant="destructive" onClick={handleDisableService} disabled={disableServiceLoading}>
                      {disableServiceLoading ? 'Disabling...' : 'Disable'}
                    </Button>
                  ) : (
                    <Button onClick={serviceForm.handleSubmit(onServiceSubmit)} disabled={enableServiceLoading}>
                      {enableServiceLoading
                        ? isModalEditable
                          ? 'Updating...'
                          : 'Enabling...'
                        : isModalEditable
                          ? 'Update'
                          : 'Enable'}
                    </Button>
                  )}
                </div>
              </div>
            </Form>
          </div>

          <Separator orientation="vertical" className="flex-shrink-0" />

          {/* Portal Section */}
          <div className="flex-1 space-y-4 min-w-0">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Portal (User)</h3>
              {!!isPortalEnabled && <Badge className="bg-green-500 text-white border-green-600">Enabled</Badge>}
            </div>
            <p className="text-sm text-muted-foreground">Use your 3PL Systems login credentials.</p>

            <Form {...portalForm}>
              <div className="space-y-4">
                <FormField
                  control={portalForm.control}
                  name="baseURL"
                  render={({
                    field
                  }: {
                    field: ControllerRenderProps<z.infer<typeof portalFormSchema>, 'baseURL'>
                  }) => (
                    <FormItem>
                      <FormLabel>Base URL</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="yourcompany.3plsystems.com"
                          {...field}
                          disabled={
                            enablePortalLoading || disablePortalLoading || (isPortalEnabled && !isModalEditable)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={portalForm.control}
                  name="username"
                  render={({
                    field
                  }: {
                    field: ControllerRenderProps<z.infer<typeof portalFormSchema>, 'username'>
                  }) => (
                    <FormItem>
                      <FormLabel>Username</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Enter your Username (email)"
                          {...field}
                          disabled={
                            enablePortalLoading || disablePortalLoading || (isPortalEnabled && !isModalEditable)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={portalForm.control}
                  name="password"
                  render={({
                    field
                  }: {
                    field: ControllerRenderProps<z.infer<typeof portalFormSchema>, 'password'>
                  }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Enter your Password"
                          {...field}
                          disabled={
                            enablePortalLoading || disablePortalLoading || (isPortalEnabled && !isModalEditable)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!!portalFormError && <Alert variant="error" description={portalFormError} />}

                <div className="flex justify-end gap-2">
                  {isPortalEnabled && !isModalEditable ? (
                    <Button variant="destructive" onClick={handleDisablePortal} disabled={disablePortalLoading}>
                      {disablePortalLoading ? 'Disabling...' : 'Disable'}
                    </Button>
                  ) : (
                    <Button onClick={portalForm.handleSubmit(onPortalSubmit)} disabled={enablePortalLoading}>
                      {enablePortalLoading
                        ? isModalEditable
                          ? 'Updating...'
                          : 'Enabling...'
                        : isModalEditable
                          ? 'Update'
                          : 'Enable'}
                    </Button>
                  )}
                </div>
              </div>
            </Form>
          </div>
        </div>

        <DialogFooter className="mt-6 flex flex-row justify-between">
          {!!app.docs && (
            <a
              href={app.docs}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
            >
              See detailed steps here
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
          <Button variant="outline" onClick={handleClose} type="button">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
